import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { FulfillmentStepKind, OrderStatus } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto, OrderFulfillmentStepDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { AcceptOnBehalfDialog } from "areas/orders/components/AcceptOnBehalfDialog";
import {
  OrderActionPreventedByUnsavedChangesDialog,
  disabledLookSx,
} from "areas/orders/components/OrderLifecycleActions";
import { formatInstantInUtc } from "areas/orders/components/OrderVersionHistory";
import { RetryBillingAction } from "areas/orders/components/RetryBillingAction";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { completeFulfilmentStep, reopenFulfilmentStep } from "areas/orders/services/fulfilmentService";
import { describeFulfilmentStatus } from "areas/orders/utils/fulfilmentStatus";
import { describeOrderError } from "areas/orders/utils/orderErrors";

/**
 * What each step of the record is called. Written as a full map so a seventh kind cannot compile
 * until somebody names it.
 */
const stepLabels: Record<FulfillmentStepKind, string> = {
  [FulfillmentStepKind.OrderAccepted]: "Order accepted",
  [FulfillmentStepKind.InternalConfirmation]: "Internal confirmation",
  [FulfillmentStepKind.BillingSetupComplete]: "Billing setup complete",
  [FulfillmentStepKind.InvoiceBalancePaid]: "Invoice balance paid",
  [FulfillmentStepKind.ProvisioningDue]: "Provisioning due",
  [FulfillmentStepKind.ManualGate]: "Manual gate",
};

/**
 * The three steps that make up the pipeline, in the order they are drawn. The other three kinds are
 * flags below it: each answers "is anything holding this up", not "how far has this got".
 */
const pipelineKinds = [
  FulfillmentStepKind.OrderAccepted,
  FulfillmentStepKind.BillingSetupComplete,
  FulfillmentStepKind.InvoiceBalancePaid,
] as const;

/**
 * Said when no fulfilment view reached this panel at all. The view's own `unavailableReason` is
 * preferred wherever there is one, because the server says why in its own words.
 */
export const fulfilmentUnavailableFallback = "Fulfilment tracking is not available yet.";

/** Said while the record is being read. It is also the marker the panel reads a pending state by. */
export const fulfilmentReadPendingMessage = "Reading the fulfilment record.";

/**
 * The view a caller passes while its read of the record is still in flight.
 *
 * A read that has not answered yet is not a record that does not exist, and the panel said the
 * same sentence for both. The state travels inside the view rather than as a prop of its own,
 * because the rail between the page and this panel passes the view straight through and types it
 * as the DTO. A caller that does not know the marker still prints the sentence, which is true.
 */
export const fulfilmentReadPending: OrderFulfillmentDto = {
  isAvailable: false,
  unavailableReason: fulfilmentReadPendingMessage,
  fulfillmentId: null,
  isFrozen: false,
  provisioningDueDate: null,
  provisionedAt: null,
  steps: [],
};

/**
 * The view a caller passes where the read of the record failed. The server's own words follow the
 * statement, so a reader can tell a failure from an order that holds no record.
 * @param reason
 */
export function fulfilmentReadFailed(reason: string): OrderFulfillmentDto {
  return { ...fulfilmentReadPending, unavailableReason: `The fulfilment record could not be read. ${reason}` };
}

/** Refused before the reset is sent, because the API requires a reason and would refuse it too. */
export const resetReasonRequiredMessage = "Give a reason for undoing this approval.";

/**
 * Said before a completion of a step this order does not need.
 *
 * The record allows it - invariant 4 of the lifecycle document, and `Fulfillment.CompleteStep`
 * carries no `IsRequired` guard - so the panel keeps the control the record offers. What the
 * reader was missing is this sentence: a tester cleared a confirmation the order never needed,
 * and nothing said what they were about to do.
 */
export const notRequiredCompletionWarning =
  "This step is not required for this order, so completing it releases nothing. The record keeps the completion and names you.";

/** Added for the internal confirmation, which no reopen can take back. */
export const irreversibleCompletionWarning =
  "Only the manual gate can be reopened. This confirmation cannot be undone.";

/**
 * Why a write on the record is offered but refused. It names the role, because a disabled
 * `Approve fulfilment` with no reason reads as an order nothing can release.
 */
export const fulfilmentWriteForbiddenMessage = `You need the ${RbacPolicies.Fulfillments.ReadWrite} role to act on a fulfilment step.`;

// Off-screen but still in the accessibility tree, so aria-describedby has something to point at.
// The same shape `OverflowMenu` uses for a policy-disabled row action.
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

const automaticModeLabel = "Automatic";
const manualModeLabel = "Manual approval";
const inProgressMeta = "In progress";
const awaitingPaymentMeta = "Awaiting payment";
const notRequiredMeta = "Not required";
const gateHeldMeta = "Held — approval required";
/** Said on the billing step, where the gate holding it is drawn as a flag of its own below. */
const heldByGateMeta = "Held";
const gateNotAppliedMeta = "Not applied";
/** Said on every open step of a record the acceptance freeze has not pinned. */
const forecastMeta = "Forecast";
const provisioningNotRaisedMeta = "Not raised";
const provisioningOutstandingMeta = "Outstanding";
const provisionedMeta = "Provisioned";

/** How a row reads: finished, the one being waited on, or not reached. */
type StepTone = "done" | "current" | "pending";

interface PipelineRow {
  kind: FulfillmentStepKind;
  tone: StepTone;
  meta: string;
}

/**
 * How a flag is drawn. The colour and the fill are the whole signal, so they are decided once here
 * rather than at each of the three call sites.
 */
interface FlagView {
  meta: string;
  /**
   * The note the record keeps on the step, printed under the state. The seeder releases a
   * confirmation with one, and the reason typed to reapply a manual gate is stored here too, so a
   * flag that showed the time alone left a reader with no idea why the step reads as it does.
   */
  note: string | null;
  colour: "success.main" | "warning.main" | "info.main" | "text.secondary";
  isDotFilled: boolean;
  isEmphasised: boolean;
}

/** The control that clears a flag, offered only where the server says staff may clear it. */
interface FlagAction {
  label: string;
  isBusy: boolean;
  onAct: () => void;
  /** Why this reader may not take the action. Undefined where they may. */
  forbiddenReason?: string;
}

function indexSteps(steps: OrderFulfillmentStepDto[]): Map<FulfillmentStepKind, OrderFulfillmentStepDto> {
  return new Map(steps.map((step) => [step.kind, step]));
}

/**
 * What a pipeline step is waiting for while it is the current one. The last step waits on the
 * customer's money rather than on us, and says so.
 * @param kind
 */
function pendingMeta(kind: FulfillmentStepKind): string {
  return kind === FulfillmentStepKind.InvoiceBalancePaid ? awaitingPaymentMeta : inProgressMeta;
}

/**
 * True while the gate still holds the order: this order needs it, and nobody has cleared it. It is
 * the state the flag prints as "Held — approval required", and the only state a completion is
 * offered in - the record reports `canComplete` on a gate that is not required as well.
 * @param gate
 */
function isGateHeld(gate?: OrderFulfillmentStepDto): boolean {
  return gate !== undefined && gate.isRequired && !gate.isComplete;
}

/**
 * The gates that stop billing setup before it starts. While either holds the order, nobody has
 * begun the billing work, so the step is held rather than in progress.
 * @param steps
 */
function isHeldByGate(steps: Map<FulfillmentStepKind, OrderFulfillmentStepDto>): boolean {
  return [FulfillmentStepKind.ManualGate, FulfillmentStepKind.InternalConfirmation].some((kind) =>
    isGateHeld(steps.get(kind))
  );
}

/**
 * The pipeline as it is drawn: the three steps the record carries for it, each with the tone that
 * decides its dot, its rail and its weight.
 *
 * The current step is the first one this order still has to complete. A step it does not have to
 * complete is skipped when finding it and reads "Not required" - an order on invoice terms never
 * pays a balance on acceptance, and a row that sat on "Awaiting payment" for ever would be a lie.
 *
 * Only the acceptance freeze pins what this order must do. Before it, the record re-stamps its
 * steps on every edit, so no step is current and every open step reads as the forecast it is - a
 * draft, a cancelled order and an expired quote all read "Order accepted — In progress" while this
 * ignored the freeze.
 * @param steps
 * @param isFrozen
 */
function buildPipeline(steps: Map<FulfillmentStepKind, OrderFulfillmentStepDto>, isFrozen: boolean): PipelineRow[] {
  const rows = pipelineKinds.map((kind) => steps.get(kind)).filter((step) => step !== undefined);
  const currentIndex = isFrozen ? rows.findIndex((step) => step.isRequired && !step.isComplete) : -1;
  const heldByGate = isHeldByGate(steps);

  return rows.map((step, index) => {
    // Only billing setup is held: a gate does not stop the order being accepted, and no balance is
    // waited on until billing is set up. A held step is not the current one either, because the
    // rail would otherwise draw work as under way that nobody has begun.
    const isHeld = heldByGate && step.kind === FulfillmentStepKind.BillingSetupComplete && !step.isComplete;
    const isCurrent = index === currentIndex && !isHeld;

    return {
      kind: step.kind,
      tone: step.isComplete ? "done" : isCurrent ? "current" : "pending",
      meta: describeStepProgress(step, isCurrent, isHeld, isFrozen),
    };
  });
}

function describeStepProgress(
  step: OrderFulfillmentStepDto,
  isCurrent: boolean,
  isHeld: boolean,
  isFrozen: boolean
): string {
  if (step.isComplete) return step.completedAt ? formatInstantInUtc(step.completedAt) : "";
  // Before the freeze the step states nothing about this order: what it needs, and whether it
  // needs it at all, is settled by the acceptance.
  if (!isFrozen) return forecastMeta;
  if (!step.isRequired) return notRequiredMeta;
  if (isHeld) return heldByGateMeta;
  return isCurrent ? pendingMeta(step.kind) : "";
}

/**
 * Who last acted on the step, or null where nobody has.
 *
 * `lastModifiedByUser` is stamped when the record is built as well as when somebody acts on it, so
 * a step nobody has touched still carries a name. The panel names it only beside an action the
 * step really records - a clearance, or a reason somebody typed - because crediting the record's
 * author with a decision nobody made is worse than naming nobody.
 * @param step
 */
function actorOf(step?: OrderFulfillmentStepDto): string | null {
  const actor = step?.lastModifiedByUser.trim();
  return actor === undefined || actor === "" ? null : actor;
}

/**
 * The note the record keeps on the step, and who left it.
 *
 * An incomplete step carries a note only where somebody typed one - the reason a manual gate was
 * reapplied - so that person is named beside it. A cleared step names its actor on the line above,
 * so its note stands alone.
 * @param step
 * @param actor
 */
function describeStepNote(step: OrderFulfillmentStepDto | undefined, actor: string | null): string | null {
  const note = step?.details?.trim();
  if (note === undefined || note === "") return null;
  return step?.isComplete || actor === null ? note : `${note} — ${actor}`;
}

/**
 * A gate as the reader needs it: cleared, by whom and when, holding and saying so, or never applied
 * to this order at all. `ManualGate` and `InternalConfirmation` are both gates, so both read the
 * same way.
 *
 * The clearance names its actor because that is the whole point of the gate: work item B6 has the
 * step record actor and time, and a `Cleared` that named nobody left a reader unable to say who
 * gave the confirmation.
 * @param step
 */
function describeGate(step?: OrderFulfillmentStepDto): FlagView {
  const actor = actorOf(step);
  const note = describeStepNote(step, actor);

  if (step?.isComplete) {
    const cleared = step.completedAt ? `Cleared ${formatInstantInUtc(step.completedAt)}` : "Cleared";
    return {
      meta: actor === null ? cleared : `${cleared} by ${actor}`,
      note,
      colour: "success.main",
      isDotFilled: true,
      isEmphasised: false,
    };
  }
  if (step?.isRequired) {
    return { meta: gateHeldMeta, note, colour: "warning.main", isDotFilled: false, isEmphasised: true };
  }
  return { meta: gateNotAppliedMeta, note, colour: "text.secondary", isDotFilled: false, isEmphasised: false };
}

/**
 * Provisioning as the reader needs it: the day it falls due, and the step's own state beside it.
 * The date is pinned on the record by the acceptance freeze, so a record with none has not been
 * accepted yet. The state comes from the step, because a date alone says neither whether this order
 * has to be provisioned nor whether it already was.
 * @param step
 * @param provisioningDueDate
 */
function describeProvisioning(step: OrderFulfillmentStepDto | undefined, provisioningDueDate: string | null): FlagView {
  // The due date follows the state rather than replacing it, so the row says both.
  const due = provisioningDueDate ? ` (due ${formatDateOnly(getOrParseDateTime(provisioningDueDate))})` : "";
  // No actor: provisioning is raised and met by the system, so there is no person to name.
  const note = describeStepNote(step, null);

  if (step?.isComplete) {
    // The day, in the same shape as the due date beside it. The pair is read as one sentence, and
    // the two formats it used to carry made it read as two facts from two different records.
    const done = step.completedAt
      ? `${provisionedMeta} ${formatDateOnly(getOrParseDateTime(step.completedAt))}`
      : provisionedMeta;
    return { meta: `${done}${due}`, note, colour: "success.main", isDotFilled: true, isEmphasised: false };
  }
  if (step && !step.isRequired) {
    return {
      meta: `${notRequiredMeta}${due}`,
      note,
      colour: "text.secondary",
      isDotFilled: false,
      isEmphasised: false,
    };
  }
  if (!provisioningDueDate) {
    return { meta: provisioningNotRaisedMeta, note, colour: "text.secondary", isDotFilled: false, isEmphasised: false };
  }
  return {
    meta: `${provisioningOutstandingMeta}${due}`,
    note,
    colour: "info.main",
    isDotFilled: false,
    isEmphasised: true,
  };
}

function PipelineStep({ row, isLast, action }: { row: PipelineRow; isLast: boolean; action?: React.ReactNode }) {
  const isDone = row.tone === "done";
  const dotColour = isDone ? "success.main" : row.tone === "current" ? "info.main" : "divider";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: action ? "12px minmax(0, 1fr) auto" : "12px minmax(0, 1fr)",
        columnGap: 1.25,
      }}
    >
      <Stack sx={{ alignItems: "center" }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            mt: 0.375,
            flex: "0 0 auto",
            borderRadius: "50%",
            border: 1,
            borderColor: dotColour,
            bgcolor: isDone ? "success.main" : "transparent",
          }}
        />
        {/* The rail. It carries the same colour as the step above it, so a finished run reads as
            one unbroken line rather than as three separate dots. */}
        {!isLast && <Box sx={{ flex: 1, width: "2px", minHeight: 12, bgcolor: isDone ? "success.main" : "divider" }} />}
      </Stack>
      <Box sx={{ pb: isLast ? 0 : 1.25 }}>
        <Typography
          variant="body2"
          color={row.tone === "pending" ? "text.secondary" : "text.primary"}
          sx={{ fontWeight: row.tone === "current" ? 600 : 400 }}
        >
          {stepLabels[row.kind]}
        </Typography>
        {row.meta && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {row.meta}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

/**
 * One flag under the pipeline. Flags are drawn apart from the pipeline on purpose: they sit on no
 * rail, because none of them is a stage an order passes through - each is a condition that either
 * holds this order up or does not apply to it.
 * @param root0
 * @param root0.label
 * @param root0.view
 * @param root0.action
 * @param root0.onReset
 */
function FulfilmentFlag({
  label,
  view,
  action,
  onReset,
}: {
  label: string;
  view: FlagView;
  action?: FlagAction;
  /** Offered only where the server says the clearance may be undone. */
  onReset?: () => void;
}) {
  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "12px minmax(0, 1fr) auto", columnGap: 1.25, alignItems: "center" }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          mt: 0.375,
          alignSelf: "start",
          borderRadius: "50%",
          border: 1,
          borderColor: view.colour,
          bgcolor: view.isDotFilled ? view.colour : "transparent",
        }}
      />
      <Box sx={{ alignSelf: "start" }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "baseline" }}>
          <Typography variant="body2" sx={{ color: view.colour, fontWeight: view.isEmphasised ? 600 : 400 }}>
            {label}
          </Typography>
          {onReset && (
            <Link component="button" type="button" variant="caption" onClick={onReset}>
              Reset
            </Link>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {view.meta}
        </Typography>
        {view.note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
            {view.note}
          </Typography>
        )}
      </Box>
      {action && <FlagActionButton action={action} />}
    </Box>
  );
}

/**
 * A flag's control. An action this reader may not take stays listed and disabled with the reason,
 * rather than vanishing: the flag beside it reads "Held — approval required", so an absent button
 * would say the order cannot be released at all instead of "not by you".
 * @param root0
 * @param root0.action
 */
function FlagActionButton({ action }: { action: FlagAction }) {
  const reasonId = React.useId();
  const button = (
    <Button
      variant="outlined"
      size="small"
      disabled={!!action.forbiddenReason}
      loading={action.isBusy}
      aria-describedby={action.forbiddenReason ? reasonId : undefined}
      onClick={action.onAct}
    >
      {action.label}
    </Button>
  );

  // A disabled Button fires no pointer events, so the tooltip hangs off a wrapper — which leaves it
  // invisible to a screen reader. The hidden span carries the same words for one to announce.
  return action.forbiddenReason ? (
    <Tooltip title={action.forbiddenReason}>
      <span>
        {button}
        <Box component="span" id={reasonId} sx={visuallyHidden}>
          {action.forbiddenReason}
        </Box>
      </span>
    </Tooltip>
  ) : (
    button
  );
}

/**
 * How far this order has got through fulfilment, and every staff action on the record.
 *
 * Three pipeline steps, then three flags. The pipeline is the run an accepted order makes -
 * accepted, billing set up, balance paid - and the flags are the conditions beside it. `Internal
 * confirmation` is a flag the approved prototype has no slot for, because the prototype predates
 * the step kind; it is here because it is the only gate that holds anything up today, and until
 * this contract existed nothing in the product could clear it.
 *
 * **`canComplete` and `canReopen` come from the server**, on every step. The reopen alone has five
 * guards, so re-deriving them here would give the reader a control the API then refuses, or hide
 * one it would have accepted. They answer for the record, not for the caller, so every write is
 * gated here on `Fulfillments.ReadWrite` as well — the role each step action demands. A step this
 * order does not need keeps its control, because completing one is allowed and recorded - see
 * {@link notRequiredCompletionWarning} - and it is warned about rather than hidden.
 *
 * **Approving the manual gate, and undoing it, release nothing downstream yet.** Provisioning needs
 * a Zuora rate plan code and subscription key, and the Order deliberately stores no billing system
 * identifiers, so the record's readiness is computed and logged and nothing acts on it. Both toasts
 * and the flag copy are the approved design, so do not soften them to match today's behaviour.
 *
 * The record is the authority wherever it can be read, so an available record renders the rail
 * alone. {@link describeFulfilmentStatus} answers from the order's own fields, and those fields
 * cannot see a release taken on the record - the copy on screen would still read as held after
 * staff cleared the hold.
 * @param root0
 * @param root0.order
 * @param root0.fulfillment
 * @param root0.onFulfillmentChanged
 */
export function FulfilmentPanel({
  order,
  fulfillment,
  onFulfillmentChanged,
  canAcceptOnBehalf,
  canAct,
  onOrderChanged,
  isDirty,
}: {
  order: OrderDto;
  /** The fulfilment record's own view of this order. */
  fulfillment?: OrderFulfillmentDto;
  /**
   * Asked to read the record again, after a write that changed it. A step write can change the
   * order as well - completing `InternalConfirmation` releases the order's own hold - so a caller
   * that renders the order beside this panel reads both.
   */
  onFulfillmentChanged: () => void;
  canAcceptOnBehalf: boolean;
  canAct: boolean;
  onOrderChanged: (order: OrderDto) => void;
  isDirty: boolean;
}) {
  const { sendNewAlert } = useAlerts();
  const { isAuthorized } = useRbacContext();
  const [error, setError] = useState<string | undefined>(undefined);
  const [busyStep, setBusyStep] = useState<FulfillmentStepKind | undefined>(undefined);
  const [isResetPrompted, setIsResetPrompted] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [isReasonMissing, setIsReasonMissing] = useState(false);
  const [resetError, setResetError] = useState<string | undefined>(undefined);
  const [isConfirmPrompted, setIsConfirmPrompted] = useState(false);
  const [confirmNote, setConfirmNote] = useState("");
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined);
  const [isApprovePrompted, setIsApprovePrompted] = useState(false);
  const [approveError, setApproveError] = useState<string | undefined>(undefined);
  const [isAcceptOnBehalfOpen, setIsAcceptOnBehalfOpen] = useState(false);
  const [isAcceptBlockedBySave, setIsAcceptBlockedBySave] = useState(false);

  const fulfillmentId = fulfillment?.isAvailable ? fulfillment.fulfillmentId : null;
  const isReadPending = fulfillment?.unavailableReason === fulfilmentReadPendingMessage;
  // The freeze is what pins the steps, so the panel reads the whole rail as a forecast without it.
  const isFrozen = fulfillment?.isFrozen ?? false;
  const steps = indexSteps(fulfillment?.steps ?? []);
  const pipeline = buildPipeline(steps, isFrozen);
  const manualGate = steps.get(FulfillmentStepKind.ManualGate);
  const internalConfirmation = steps.get(FulfillmentStepKind.InternalConfirmation);
  const provisioning = steps.get(FulfillmentStepKind.ProvisioningDue);
  // Every step action demands this role of the caller, and the record's own canComplete/canReopen
  // cannot see who is asking. Without it the panel stays a reader's view of the record.
  const forbiddenReason = isAuthorized(RbacPolicies.Fulfillments.ReadWrite)
    ? undefined
    : fulfilmentWriteForbiddenMessage;

  const complete = async (step: FulfillmentStepKind, toast: string) => {
    if (!fulfillmentId) return;
    setError(undefined);
    setBusyStep(step);
    try {
      await completeFulfilmentStep(fulfillmentId, step);
      onFulfillmentChanged();
      sendNewAlert(toast, AlertSeverity.Success);
    } catch (completionError) {
      setError(describeOrderError(completionError));
    } finally {
      setBusyStep(undefined);
    }
  };

  /**
   * Completes a step from a prompt. The failure stays inside the prompt, where the reader is
   * looking, rather than under the flags behind it.
   * @param step
   * @param toast
   * @param details
   * @param report
   * @param close
   */
  const completeFromPrompt = async (
    step: FulfillmentStepKind,
    toast: string,
    details: string | undefined,
    report: (message?: string) => void,
    close: () => void
  ) => {
    if (!fulfillmentId) return;
    report(undefined);
    try {
      await completeFulfilmentStep(fulfillmentId, step, details);
      close();
      onFulfillmentChanged();
      sendNewAlert(toast, AlertSeverity.Success);
    } catch (completionError) {
      report(describeOrderError(completionError));
    }
  };

  const promptConfirm = () => {
    setConfirmNote("");
    setConfirmError(undefined);
    setIsConfirmPrompted(true);
  };

  /**
   * Clears the internal-confirmation hold, with the note the staff member typed. The note is
   * optional: the API stores it on the step, and the flag prints it back, but a confirmation is
   * valid without one.
   */
  const confirmInternally = () =>
    completeFromPrompt(
      FulfillmentStepKind.InternalConfirmation,
      orderToasts.internalConfirmationRecorded,
      confirmNote.trim() || undefined,
      setConfirmError,
      () => setIsConfirmPrompted(false)
    );

  const promptApprove = () => {
    setApproveError(undefined);
    setIsApprovePrompted(true);
  };

  /** Approves the manual gate from the prompt, which only a gate this order does not need opens. */
  const approveGate = () =>
    completeFromPrompt(FulfillmentStepKind.ManualGate, orderToasts.fulfilmentApproved, undefined, setApproveError, () =>
      setIsApprovePrompted(false)
    );

  const promptReset = () => {
    setResetReason("");
    setIsReasonMissing(false);
    setResetError(undefined);
    setIsResetPrompted(true);
  };

  const confirmReset = async () => {
    const reason = resetReason.trim();
    if (!fulfillmentId) return;
    if (reason === "") {
      setIsReasonMissing(true);
      return;
    }
    setIsReasonMissing(false);
    setResetError(undefined);
    try {
      await reopenFulfilmentStep(fulfillmentId, FulfillmentStepKind.ManualGate, reason);
      setIsResetPrompted(false);
      onFulfillmentChanged();
      sendNewAlert(orderToasts.manualGateReapplied, AlertSeverity.Success);
    } catch (reopenError) {
      setResetError(describeOrderError(reopenError));
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "baseline", justifyContent: "space-between" }}>
          <Typography variant="h6" component="h2">
            Fulfilment
          </Typography>
          {/* The order's own choice, pinned by the acceptance freeze, so it cannot go stale here. */}
          <Typography variant="caption" color="text.secondary">
            {order.requiresManualFulfillmentApproval ? manualModeLabel : automaticModeLabel}
          </Typography>
        </Stack>

        {fulfillmentId ? (
          <>
            {/* What the order itself says, where the record cannot answer for it. A cancelled or
                expired order runs no pipeline at all, and its forecast rail alone read as work
                somebody had started. */}
            {!isFrozen && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2">{describeFulfilmentStatus(order).headline}</Typography>
                {canAcceptOnBehalf && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={isDirty ? (theme) => disabledLookSx(theme, "outlined") : undefined}
                    onClick={() => (isDirty ? setIsAcceptBlockedBySave(true) : setIsAcceptOnBehalfOpen(true))}
                  >
                    Accept on behalf
                  </Button>
                )}
              </Stack>
            )}
            <Box>
              {pipeline.map((row, index) => (
                <PipelineStep
                  key={row.kind}
                  row={row}
                  isLast={index === pipeline.length - 1}
                  action={
                    row.kind === FulfillmentStepKind.BillingSetupComplete &&
                    canAct &&
                    order.status === OrderStatus.Accepted ? (
                      <RetryBillingAction odNumber={order.odNumber} />
                    ) : undefined
                  }
                />
              ))}
            </Box>

            <Stack spacing={0.75}>
              <FulfilmentFlag
                label={stepLabels[FulfillmentStepKind.ManualGate]}
                view={describeGate(manualGate)}
                // Offered wherever the record offers it, a gate this order does not need included:
                // completing one is allowed and recorded, so a hidden control would disagree with
                // the API. A gate that holds the order is approved at once, because that act is
                // the routine one; the other goes through the warning.
                action={
                  manualGate?.canComplete
                    ? {
                        label: "Approve fulfilment",
                        isBusy: busyStep === FulfillmentStepKind.ManualGate,
                        onAct: isGateHeld(manualGate)
                          ? () => complete(FulfillmentStepKind.ManualGate, orderToasts.fulfilmentApproved)
                          : promptApprove,
                        forbiddenReason,
                      }
                    : undefined
                }
                // Dropped rather than disabled: the flag already reads "Cleared", which is the whole
                // truth about the gate, so no missing control is implied by its absence.
                onReset={manualGate?.canReopen && !forbiddenReason ? promptReset : undefined}
              />
              <FulfilmentFlag
                label={stepLabels[FulfillmentStepKind.ProvisioningDue]}
                view={describeProvisioning(provisioning, fulfillment?.provisioningDueDate ?? null)}
              />
              <FulfilmentFlag
                label={stepLabels[FulfillmentStepKind.InternalConfirmation]}
                view={describeGate(internalConfirmation)}
                action={
                  internalConfirmation?.canComplete
                    ? {
                        label: "Confirm",
                        // The prompt carries the busy state, because the write is sent from there.
                        isBusy: false,
                        onAct: promptConfirm,
                        forbiddenReason,
                      }
                    : undefined
                }
              />
            </Stack>

            {!!error && <Alert severity="error">{error}</Alert>}
          </>
        ) : isReadPending ? (
          <PendingRead />
        ) : (
          <UnreadableRecord order={order} fulfillment={fulfillment} />
        )}
      </Stack>

      <ActionDialog
        title="Confirm this order?"
        description={
          <>
            This clears the staff confirmation hold on {order.billingAccountLegalName}, so billing setup and
            provisioning can start. The note is optional, and the record keeps it.
            {!isGateHeld(internalConfirmation) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">{notRequiredCompletionWarning}</Typography>
                <Typography variant="body2">{irreversibleCompletionWarning}</Typography>
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Note (optional)"
              value={confirmNote}
              onChange={(event) => setConfirmNote(event.target.value)}
              sx={{ mt: 2 }}
            />
            {!!confirmError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {confirmError}
              </Alert>
            )}
          </>
        }
        show={isConfirmPrompted}
        confirmText="Record confirmation"
        confirmColor="primary"
        onConfirm={confirmInternally}
        onCancel={() => setIsConfirmPrompted(false)}
      />

      <ActionDialog
        title="Approve a step this order does not need?"
        description={
          <>
            <Alert severity="warning">
              <Typography variant="body2">{notRequiredCompletionWarning}</Typography>
            </Alert>
            {!!approveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {approveError}
              </Alert>
            )}
          </>
        }
        show={isApprovePrompted}
        confirmText="Approve fulfilment"
        confirmColor="primary"
        onConfirm={approveGate}
        onCancel={() => setIsApprovePrompted(false)}
      />

      <ActionDialog
        title="Reapply the manual gate?"
        description={
          <>
            This undoes the fulfilment approval for {order.billingAccountLegalName}, so the gate holds provisioning
            again. The reason is kept on the record.
            <TextField
              required
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={resetReason}
              onChange={(event) => setResetReason(event.target.value)}
              error={isReasonMissing}
              helperText={isReasonMissing ? resetReasonRequiredMessage : undefined}
              sx={{ mt: 2 }}
            />
            {!!resetError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {resetError}
              </Alert>
            )}
          </>
        }
        show={isResetPrompted}
        confirmText="Reapply gate"
        onConfirm={confirmReset}
        onCancel={() => setIsResetPrompted(false)}
      />

      <OrderActionPreventedByUnsavedChangesDialog
        open={isAcceptBlockedBySave}
        onDismiss={() => setIsAcceptBlockedBySave(false)}
      />

      <AcceptOnBehalfDialog
        order={order}
        open={isAcceptOnBehalfOpen}
        onClose={() => setIsAcceptOnBehalfOpen(false)}
        onAccepted={(accepted) => {
          onOrderChanged(accepted);
          setIsAcceptOnBehalfOpen(false);
          sendNewAlert(orderToasts.accepted, AlertSeverity.Success);
        }}
      />
    </Paper>
  );
}

/** What the panel shows while the read of the record is still in flight. */
function PendingRead() {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <CircularProgress size={16} />
      <Typography variant="body2" color="text.secondary">
        {fulfilmentReadPendingMessage}
      </Typography>
    </Stack>
  );
}

/**
 * What the panel shows where there is no record to read: why no licence has issued, from the fields
 * the order itself carries, and the server's own reason for the gap.
 * @param root0
 * @param root0.order
 * @param root0.fulfillment
 */
function UnreadableRecord({ order, fulfillment }: { order: OrderDto; fulfillment?: OrderFulfillmentDto }) {
  const { headline, detail } = describeFulfilmentStatus(order);

  return (
    <Stack spacing={1}>
      <Typography variant="body1">{headline}</Typography>
      {detail && (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      )}
      <Alert severity="info">{fulfillment?.unavailableReason ?? fulfilmentUnavailableFallback}</Alert>
    </Stack>
  );
}
